export type TimerCallback = (remaining: number) => void;
export type TimerEndCallback = () => void;

/**
 * 计时器管理器 - 管理考试倒计时
 */
export class TimerManager {
  private duration: number; // 总时长（毫秒）
  private remaining: number; // 剩余时间（毫秒）
  private startTime: number = 0;
  private timerId: ReturnType<typeof setInterval> | null = null;
  private isRunning: boolean = false;
  private isPaused: boolean = false;

  private onTick?: TimerCallback;
  private onEnd?: TimerEndCallback;

  /**
   * @param durationMinutes 考试时长（分钟）
   */
  constructor(durationMinutes: number) {
    this.duration = durationMinutes * 60 * 1000;
    this.remaining = this.duration;
  }

  /**
   * 设置计时回调
   */
  setCallbacks(onTick?: TimerCallback, onEnd?: TimerEndCallback): void {
    this.onTick = onTick;
    this.onEnd = onEnd;
  }

  /**
   * 开始计时
   */
  start(): void {
    if (this.isRunning) return;

    this.isRunning = true;
    this.isPaused = false;
    this.startTime = Date.now();

    this.timerId = setInterval(() => {
      const elapsed = Date.now() - this.startTime;
      this.remaining = Math.max(0, this.duration - elapsed);

      if (this.onTick) {
        this.onTick(this.remaining);
      }

      if (this.remaining <= 0) {
        this.stop();
        if (this.onEnd) {
          this.onEnd();
        }
      }
    }, 1000);
  }

  /**
   * 暂停计时
   */
  pause(): void {
    if (!this.isRunning || this.isPaused) return;

    this.isPaused = true;
    this.duration = this.remaining;

    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  /**
   * 继续计时
   */
  resume(): void {
    if (!this.isPaused) return;

    this.isPaused = false;
    this.startTime = Date.now();

    this.timerId = setInterval(() => {
      const elapsed = Date.now() - this.startTime;
      this.remaining = Math.max(0, this.duration - elapsed);

      if (this.onTick) {
        this.onTick(this.remaining);
      }

      if (this.remaining <= 0) {
        this.stop();
        if (this.onEnd) {
          this.onEnd();
        }
      }
    }, 1000);
  }

  /**
   * 停止计时
   */
  stop(): void {
    this.isRunning = false;
    this.isPaused = false;

    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  /**
   * 重置计时器
   */
  reset(): void {
    this.stop();
    this.remaining = this.duration;
  }

  /**
   * 获取剩余时间（毫秒）
   */
  getRemaining(): number {
    return this.remaining;
  }

  /**
   * 获取剩余时间（格式化）
   */
  getRemainingFormatted(): string {
    return TimerManager.formatTime(this.remaining);
  }

  /**
   * 获取已用时间（毫秒）
   */
  getElapsed(): number {
    return this.duration - this.remaining;
  }

  /**
   * 检查是否正在运行
   */
  getIsRunning(): boolean {
    return this.isRunning && !this.isPaused;
  }

  /**
   * 检查是否暂停
   */
  getIsPaused(): boolean {
    return this.isPaused;
  }

  /**
   * 格式化时间显示
   */
  static formatTime(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes
        .toString()
        .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
}
